import { useState, useEffect, useCallback } from 'react'
import { isIOS, isStandalone } from '../lib/pwaUtils'
import {
  getVapidPublicKey,
  listPushSubscriptions,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/api'

export interface PushSubscriptionState {
  /** Whether push is supported in this browser/context */
  isSupported: boolean
  /** Whether push is currently enabled (subscribed) */
  isSubscribed: boolean
  /** Whether an operation is in progress */
  isLoading: boolean
  /** Current Notification.permission state */
  permission: NotificationPermission | 'unsupported'
  /** Enable push notifications (requests permission if needed) */
  subscribe: () => Promise<void>
  /** Disable push notifications */
  unsubscribe: () => Promise<void>
  /** Error message if last operation failed */
  error: string | null
  /** True if iOS and not in standalone mode (push won't work) */
  needsInstallFirst: boolean
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export function usePushSubscription(): PushSubscriptionState {
  const supported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window

  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    supported ? Notification.permission : 'unsupported'
  )
  const [error, setError] = useState<string | null>(null)

  const needsInstallFirst = isIOS() && !isStandalone()

  // Check existing subscription on mount
  useEffect(() => {
    if (!supported) return
    let cancelled = false

    async function checkExisting() {
      try {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        if (!subscription) {
          if (!cancelled) setIsSubscribed(false)
          return
        }
        // Browser has a sub — but the backend row could be missing (DB reset,
        // manual delete, branch with empty DB). If toggle showed "on" without
        // a backend row, push delivery would silently no-op. Verify the
        // backend has this endpoint and re-POST if not (UPSERT is idempotent).
        try {
          const rows = await listPushSubscriptions()
          const presentInBackend = rows.some((r) => r.endpoint === subscription.endpoint)
          if (!presentInBackend) {
            const rawKeys = subscription.toJSON()
            const p256dh = rawKeys.keys?.p256dh
            const auth = rawKeys.keys?.auth
            if (p256dh && auth) {
              await subscribeToPush({ endpoint: subscription.endpoint, p256dh, auth })
            }
          }
        } catch {
          // List/recover failed (network, 401, etc.) — fall back to browser
          // state. Worst case the toggle reflects browser truth and the user
          // can re-toggle to force a re-POST.
        }
        if (!cancelled) setIsSubscribed(true)
      } catch {
        // serviceWorker.ready / getSubscription failed — leave default false.
      }
    }

    checkExisting()
    return () => { cancelled = true }
  }, [supported])

  const subscribe = useCallback(async () => {
    if (!supported || needsInstallFirst) return
    setIsLoading(true)
    setError(null)

    // Track which step we're on so a hang shows up with a useful breadcrumb.
    let step = 'starting'
    const setStep = (s: string) => { step = s }

    // Hard outer timeout — covers EVERY await in the flow including
    // Notification.requestPermission() and navigator.serviceWorker.ready
    // which can both hang on iOS PWA.
    const hardTimeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`Timed out after 20s at step: ${step}`)), 20_000)
    })

    const work = async (): Promise<void> => {
      setStep('Notification.permission')
      // Skip the prompt if we already know the answer — avoids iOS hangs
      // when iOS thinks it has nothing new to ask.
      let perm: NotificationPermission = Notification.permission
      if (perm === 'default') {
        setStep('requestPermission')
        perm = await Notification.requestPermission()
      }
      setPermission(perm)
      if (perm !== 'granted') {
        const where = isIOS()
          ? 'iPhone Settings → Notifications → CC Platform'
          : "your browser's notification settings (click the lock icon in the address bar)"
        throw new Error(`Permission was '${perm}' — re-enable in ${where}`)
      }

      setStep('getVapidPublicKey')
      const { public_key } = await getVapidPublicKey()
      if (!public_key) {
        throw new Error('Server returned no VAPID public key — push not configured')
      }

      setStep('serviceWorker.ready')
      const registration = await navigator.serviceWorker.ready

      setStep('pushManager.subscribe')
      const applicationServerKey = urlBase64ToUint8Array(public_key) as BufferSource
      let subscription: PushSubscription
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        })
      } catch (err) {
        // If VAPID public key was rotated server-side, the existing browser
        // subscription is bound to the old applicationServerKey. iOS/Chrome
        // throw InvalidStateError. Drop the stale sub and retry once.
        if (err instanceof DOMException && err.name === 'InvalidStateError') {
          setStep('pushManager.subscribe (retry after VAPID rotation)')
          const stale = await registration.pushManager.getSubscription()
          if (stale) await stale.unsubscribe()
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          })
        } else {
          throw err
        }
      }

      setStep('subscribeToPush (backend)')
      const rawKeys = subscription.toJSON()
      const p256dh = rawKeys.keys?.p256dh
      const auth = rawKeys.keys?.auth
      if (!p256dh || !auth) {
        // Browser handed us a subscription without keys — push won't work.
        // Throw early so the user sees a real error instead of silent failure.
        throw new Error('Browser returned subscription without encryption keys')
      }
      await subscribeToPush({
        endpoint: subscription.endpoint,
        p256dh,
        auth,
      })

      setIsSubscribed(true)
    }

    try {
      await Promise.race([work(), hardTimeout])
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to enable push notifications'
      setError(msg)
      if (typeof Notification !== 'undefined') setPermission(Notification.permission)
    } finally {
      setIsLoading(false)
    }
  }, [supported, needsInstallFirst])

  const unsubscribe = useCallback(async () => {
    if (!supported) return
    setIsLoading(true)
    setError(null)

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe()
        // Remove from backend
        await unsubscribeFromPush(subscription.endpoint)
      }
      setIsSubscribed(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to disable push notifications'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [supported])

  return {
    isSupported: supported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
    error,
    needsInstallFirst,
  }
}
